/**
 * furnish_packs — what a bought pack seats, PURE (2026-09-23).
 *
 * A player structure's interior is EMPTY until its owner buys a furnish pack (1..5) from the
 * structure console. A pack seats the first `share` of every floor's purpose plan (the plan lists
 * its pieces in the order they matter — the bed before the plant), on the pack's `floors` lowest
 * floors, so a bigger pack is a fuller room and never a different one. Deterministic: the same
 * pack in the same room is the same furniture on every client.
 */
import { FURNISH_PACKS, furnishPack, type FurnishPack } from '@shared/types/structures.js';
import { purposePlan, drawnHalf, type PlanSlot, type PlanFrame } from './interior_plans.js';
import { consoleSeat } from './structure_access.js';
import type { InteriorPurpose } from './interior_purpose.js';

export { FURNISH_PACKS, furnishPack };
export type { FurnishPack };

/** How many of a floor's `n` plan slots a pack seats: the share rounded up, at least one if there are any. */
export function packSlotCount(pack: FurnishPack, n: number): number {
  if (n <= 0) return 0;
  return Math.max(1, Math.min(n, Math.ceil(n * pack.share)));
}

/** The slots a pack seats on one floor (the plan's order is its priority). */
export function packSlots(pack: FurnishPack, floorSlots: readonly PlanSlot[]): PlanSlot[] {
  return floorSlots.slice(0, packSlotCount(pack, floorSlots.length));
}

/** The floors a pack furnishes in a building of `floors` floors (never more than the plan's three). */
export function packFloors(pack: FurnishPack, floors: number): number {
  return Math.max(0, Math.min(floors, 3, pack.floors));
}

/** The whole furnishing a pack gives THIS room: per floor, the seated slots. */
/** THE CONSOLE'S GROUND (2026-09-24): the structure console stands at the back wall (`consoleSeat`) with the
 *  player before it, and E belongs to the NEAREST piece — a sofa the plan seated on that spot won E from the
 *  console every time (the furniture probe). No pack piece may take the console's box or the standing room
 *  in front of it; the plan's order is kept, so a pack is still a prefix of the next. */
export const CONSOLE_KEEP_W = 0.9, CONSOLE_KEEP_FRONT = 1.9, CONSOLE_KEEP_BACK = 0.5;
export function offTheConsole(slots: readonly PlanSlot[], frame: PlanFrame): PlanSlot[] {
  const c = consoleSeat({ hw: frame.hw, hd: frame.hd, stair: { x: frame.stair.x, z: frame.stair.z, w: frame.stair.w ?? 1.6, d: frame.stair.d } });
  return slots.filter((s) => {
    const { hx, hz } = drawnHalf(s.key, s.yaw);
    const meetX = Math.abs(s.x - c.x) <= CONSOLE_KEEP_W + hx;
    const meetZ = s.z + hz >= c.z - CONSOLE_KEEP_FRONT && s.z - hz <= c.z + CONSOLE_KEEP_BACK;
    return !(meetX && meetZ);
  });
}

export function packPlan(pack: FurnishPack, purpose: InteriorPurpose, frame: PlanFrame, floors: number): PlanSlot[][] {
  const out: PlanSlot[][] = [];
  for (let f = 0; f < packFloors(pack, floors); f++) {
    const plan = purposePlan(purpose, frame, f);
    out.push(packSlots(pack, f === 0 ? offTheConsole(plan, frame) : plan)); // the console stands on the ground floor
  }
  return out;
}

/** How many pieces each pack would seat in this room — what the console prints beside each price. */
export function packItemCounts(purpose: InteriorPurpose, frame: PlanFrame, floors: number): Array<{ pack: FurnishPack; items: number }> {
  return FURNISH_PACKS.map((pack) => ({ pack, items: packPlan(pack, purpose, frame, floors).reduce((a, s) => a + s.length, 0) }));
}
